import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Row,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import Link from 'next/link';
import { IAreaGraph, ITransaction } from '@/types/explorer';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/shadcn/table';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/shadcn/card';
import { sanitizeUsername } from '@/utils/strings';
import { DateFormats, formatDate, prettyDate } from '@/utils/dates';
import { Button } from '@/components/shadcn/button';
import { getTransactions } from '@/api/transactions';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/shadcn/breadcrumb';
import { getContributorStats } from '@/api/contributors';
import {
  formatNumber,
  getDefaultByWeekData,
  getTotalKm,
  transformContributorStats,
} from '@/utils/explorer';
import { routes } from '@/constants/urls';
import { P } from '@/components/shadcn/typography';
import { getIntegerTicksForAreaChart } from '@/utils/charts';
import CustomTooltip from '../charts/CustomTooltip';
import AreaChart from '../charts/AreaChart';
import DataBox from '../shared/DataBox';

const LIMIT = 100;

const columnHelper = createColumnHelper<ITransaction>();
const cols = [
  {
    id: 'amount',
    header: 'HONEY',
    enableSorting: true,
    cell: ({ row }: { row: Row<ITransaction> }) => (
      <div className="flex gap-2 overflow-hidden text-ellipsis">
        <span className="text-xs md:text-sm">
          {(row.getValue('amount') as number)?.toFixed(2) || 0}
        </span>
      </div>
    ),
  },
  {
    id: 'type',
    header: 'Reward Type',
    enableSorting: true,
    sortDescFirst: true,
    cell: ({ row }: { row: Row<ITransaction> }) => (
      <div className="truncate text-xs md:text-sm">{row.getValue('type')}</div>
    ),
  },
  {
    id: 'createdAt',
    header: 'Date',
    enableSorting: true,
    sortDescFirst: true,
    cell: ({ row }: { row: Row<ITransaction> }) => (
      <div className="text-xs md:text-sm">
        {prettyDate(row.getValue('createdAt'))}
      </div>
    ),
  },
  {
    id: 'signature',
    header: 'Transaction',
    enableSorting: true,
    sortDescFirst: true,
    cell: ({ row }: { row: Row<ITransaction> }) => (
      <Link
        target="_blank"
        href={`https://explorer.solana.com/tx/${row.getValue('signature')}`}
        className="block overflow-hidden text-ellipsis text-xs text-azure-400 md:text-sm">
        {row.getValue('signature')}
      </Link>
    ),
  },
];

interface Props {
  username: string;
}

export default function Contributor(props: Props) {
  const { username } = props;
  const [transactions, setTransactions] = useState<ITransaction[]>([]);
  const [pagination, setPagination] = useState({
    pageSize: LIMIT,
    pageIndex: 0,
  });
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tokensByWeek, setTokensByWeek] = useState<IAreaGraph[]>([]);
  const [coverageByWeek, setCoverageByWeek] = useState<IAreaGraph[]>([]);
  const [mountReviewByWeek, setMountReviewByWeek] = useState<IAreaGraph[]>([]);
  const [joinedDate, setJoinedDate] = useState<string | null>(null);

  const currentTokens =
    tokensByWeek[(tokensByWeek.length || 1) - 1]?.value || 0;
  const currentCoverage =
    coverageByWeek[(tokensByWeek.length || 1) - 1]?.value || 0;
  const currentMountReview =
    mountReviewByWeek[(mountReviewByWeek.length || 1) - 1]?.value || 0;

  const columns = useMemo(
    () =>
      cols.map(({ id, header, cell, enableSorting, sortDescFirst }: any) => ({
        ...columnHelper.accessor(id, {
          header,
        }),
        cell,
        enableSorting,
        sortDescFirst,
      })),
    [],
  );

  const fetchList = useCallback(
    async (skip = 0, username: string) => {
      setIsLoading(true);
      const _transactions: ITransaction[] = await getTransactions(
        LIMIT,
        skip,
        username,
      );
      setHasNextPage(_transactions.length === LIMIT);
      setTransactions(_transactions);
      setIsLoading(false);
      setIsDataLoaded(true);
    },
    [setTransactions],
  );

  useEffect(() => {
    fetchList(pagination.pageIndex * LIMIT, username);
  }, [fetchList, pagination.pageIndex, username]);

  const fetchStats = useCallback(async (username: string) => {
    setIsLoading(true);
    const stats = await getContributorStats(username);
    if (stats) {
      const _tokensByWeek = transformContributorStats(
        stats.tokensByWeek || getDefaultByWeekData(0),
      );
      const _mountReviewByWeek = transformContributorStats(
        stats.mountReviews || getDefaultByWeekData(0),
      );
      const _coverageByWeek = transformContributorStats(
        stats.totalL12ByWeek || getDefaultByWeekData(0),
        ({ date, value }) => ({
          date,
          value: getTotalKm(value || 0),
        }),
      );
      const joinedDate = stats.createdAt || stats.updatedAt;
      setTokensByWeek(_tokensByWeek);
      setCoverageByWeek(_coverageByWeek);
      setMountReviewByWeek(_mountReviewByWeek);
      setJoinedDate(joinedDate);
    } else {
      setTokensByWeek(transformContributorStats(getDefaultByWeekData(0)));
      setCoverageByWeek(transformContributorStats(getDefaultByWeekData(0)));
      setMountReviewByWeek(transformContributorStats(getDefaultByWeekData(0)));
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchStats(username);
  }, [username, fetchStats]);

  const tableData = React.useMemo(
    () => (isLoading ? [] : transactions),
    [isLoading, transactions],
  );

  const table = useReactTable({
    data: tableData,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    onPaginationChange: setPagination,
    rowCount: -1,
    state: {
      pagination,
    },
  });

  const from =
    table.getState().pagination.pageIndex *
      table.getState().pagination.pageSize +
    1;
  const to =
    from +
    Math.min(
      (table.getState().pagination.pageIndex + 1) *
        table.getState().pagination.pageSize,
      transactions.length - 1,
    );

  const summaryText = `${sanitizeUsername(username)} has mapped ${formatNumber(
    currentCoverage,
    'short',
    true,
  )} KM and earned ${formatNumber(currentTokens, 'short', true)} HONEY.`;

  return (
    <div className="flex h-full flex-col gap-2">
      <Breadcrumb className="flex h-6 items-center lg:hidden">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link href={routes.network.contributors}>Network</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link href={routes.network.contributors}>Contributors</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{sanitizeUsername(username)}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex size-full max-w-2xl flex-col-reverse gap-2 pb-3 md:gap-6 md:pb-8 lg:flex-row">
        <div className="no-scrollbar flex size-full flex-col gap-4 overflow-visible text-neutral-0 lg:w-1/2 lg:overflow-auto">
          <Breadcrumb className="hidden h-9 shrink-0 items-center md:flex">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link href={routes.network.contributors}>Network</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link href={routes.network.contributors}>Contributors</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{sanitizeUsername(username)}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="flex flex-1 flex-col gap-2 md:grid md:grid-cols-2 md:gap-4">
            <Card className="col-span-2 flex h-full flex-col gap-1 rounded-lg bg-neutral-900 p-3 md:p-6">
              <CardHeader className="p-0">
                <CardTitle className="text-xs font-thin md:text-sm">
                  Total KM Mapped
                </CardTitle>
              </CardHeader>
              <CardContent className="flex h-[200px] grow-0 flex-col gap-3 p-0 md:h-[260px] md:gap-4">
                <p className="text-base font-thin uppercase text-azure-400 md:text-3xl">
                  {formatNumber(currentCoverage, 'short', true) || 0}
                </p>
                <div className="size-full flex-1">
                  <AreaChart
                    showYAxis
                    yAxisProps={{
                      interval: 0,
                    }}
                    data={coverageByWeek}
                    customTooltip={(props: any) => (
                      <CustomTooltip
                        line1={`${Math.round(
                          props?.payload[0]?.value || 0,
                        ).toLocaleString('en-US')} Total KM Mapped`}
                        {...props}
                      />
                    )}
                  />
                </div>
              </CardContent>
            </Card>
            <Card className="flex h-full flex-1 flex-col gap-1 rounded-lg bg-neutral-900 p-3 md:p-6">
              <CardHeader className="p-0">
                <CardTitle className="text-xs font-thin md:text-sm">
                  HONEY Rewards
                </CardTitle>
              </CardHeader>
              <CardContent className="flex h-[200px] grow-0 flex-col gap-3 p-0 md:h-full md:gap-4">
                <p className="text-base font-thin uppercase text-azure-400 md:text-3xl">
                  {formatNumber(currentTokens, 'short', true) || 0}
                </p>
                <div className="size-full flex-1">
                  <AreaChart
                    showYAxis
                    data={tokensByWeek}
                    customTooltip={(props: any) => (
                      <CustomTooltip
                        line1={`${Math.round(
                          props?.payload[0]?.value || 0,
                        ).toLocaleString('en-US')} Total Tokens Earned`}
                        {...props}
                      />
                    )}
                  />
                </div>
              </CardContent>
            </Card>
            <Card className="flex h-full flex-1 flex-col gap-1 rounded-lg bg-neutral-900 p-3 md:p-6">
              <CardHeader className="p-0">
                <CardTitle className="text-xs font-thin md:text-sm">
                  Mount Rating
                </CardTitle>
              </CardHeader>
              <CardContent className="flex h-[200px] flex-col gap-3 p-0 md:h-full md:gap-4">
                <p className="text-base font-thin uppercase text-azure-400 md:text-3xl">
                  {formatNumber(currentMountReview, 'short', true) || 0}
                </p>
                <div className="size-full flex-1">
                  <AreaChart
                    showYAxis
                    xAxisProps={{
                      interval: 7,
                    }}
                    yAxisProps={{
                      ticks: getIntegerTicksForAreaChart(mountReviewByWeek),
                    }}
                    data={mountReviewByWeek}
                    customTooltip={(props: any) => (
                      <CustomTooltip
                        line1={`Mount Rating: ${Math.round(
                          props?.payload[0]?.value || 0,
                        ).toLocaleString('en-US')}`}
                        {...props}
                      />
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            <DataBox
              className="col-span-2 w-full grow-0"
              title="Joined"
              value={
                joinedDate
                  ? formatDate(joinedDate, DateFormats.monthYear)
                  : 'N/A'
              }
            />
          </div>
        </div>
        <div className="flex w-full flex-col gap-2 md:gap-4 lg:h-full lg:w-1/2">
          <div className="hidden h-9 shrink-0 items-center lg:flex">
            <P>{summaryText}</P>
          </div>
          <Card className="no-scrollbar h-[360px] overflow-scroll border border-neutral-800 bg-neutral-900 md:h-full [&>div]:overflow-visible">
            <Table className="table-fixed">
              <TableHeader className="sticky top-0 bg-neutral-900">
                {table.getHeaderGroups().map(headerGroup => (
                  <TableRow className="text-neutral-0" key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => {
                      return (
                        <TableHead
                          data-order={index}
                          key={header.id}
                          className="h-6 w-1/4 p-3 data-[order='1']:w-1/3 data-[order='2']:w-[10%] md:h-12 md:px-4">
                          <div className="flex items-center gap-1 px-0 text-[10px] leading-none text-neutral-0 md:text-sm">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </div>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {isDataLoaded ? (
                  isLoading ? null : transactions.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="p-2 text-xs md:px-4 md:py-3 md:text-sm">
                        No transactions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    table.getRowModel().rows.map(row => (
                      <TableRow
                        key={row.id}
                        className="leading-none hover:bg-neutral-900">
                        {row.getVisibleCells().map(cell => (
                          <TableCell
                            key={cell.id}
                            className="p-2 md:px-4 md:py-3">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )
                ) : null}
              </TableBody>
            </Table>
          </Card>
          {isDataLoaded ? (
            <Card className="flex w-full items-center justify-between rounded-lg border border-neutral-800 bg-neutral-900 p-4 px-3 py-2 text-neutral-0">
              <div className="flex-1 text-[10px] md:text-xs">
                {from === 1 && to < LIMIT
                  ? `${to} transaction${to === 1 ? '' : 's'}`
                  : `${from} to ${to} transactions`}
              </div>
              <div className="space-x-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={table.previousPage}
                  className="text-[10px] text-neutral-0 md:text-xs"
                  disabled={!table.getCanPreviousPage()}>
                  Previous
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="text-[10px] text-neutral-0 md:text-xs"
                  onClick={table.nextPage}
                  disabled={!hasNextPage}>
                  Next
                </Button>
              </div>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}
